<template>
  <v-container>
    <v-row>
      <v-col
        cols="6"
        sm="3"
      >
        <v-btn
          rounded
          dense
          color="primary"
          @click="handleAddContract"
          :disabled="!areas.length"
        >
          <v-icon> mdi-plus </v-icon>
          {{ $t('common.addNew') }}
        </v-btn>
      </v-col>
      <v-col>
        <v-alert
          v-if="!areas.length"
          dense
          outlined
          type="error"
          :style="{ width: '100%' }"
          class="mb-0"
        >
         {{ $t('contract.noAreasAlert') }}
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-alert
          v-if="!contracts.length"
          dense
          outlined
          type="info"
          :style="{ width: '100%' }"
          class="mb-0"
        >
          {{ $t('contract.noContractsAlert') }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="8">
        <v-combobox
          v-model="search"
          :items="searchCriteria"
          item-text="label"
          item-value="key"
          outlined
          multiple
          return-object
          chips
          small-chips
          deletable-chips
          :label="$t('contract.search')"
          :disabled="!areas.length || !contracts.length"
          hide-details
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title><span v-html="$t('contract.searchByNameTip')"></span></v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </template>
        </v-combobox>
      </v-col>
      <v-col md="4">
        <v-autocomplete
          outlined
          :items="sortOptions"
          :label="$t('contract.sort')"
          v-model="selectedSortType"
          item-text="label"
          item-value="type"
          :append-outer-icon="sortAsc ? 'mdi-sort-ascending' : 'mdi-sort-descending'"
          @click:append-outer="sortAsc = !sortAsc"
          hide-details
        />
      </v-col>
    </v-row>
    <v-col class="mb-6">
      <span class="text-caption">{{ $t('contract.filters.title') }}</span>
      <v-row class="mt-0 pb- border rounded" style="border-color: rgba(0,0,0,0.3);">
        <v-col class="">
          <v-row align="center" class="ma-1">
            <MultiButtonFilter
              v-model="filters.status"
              :label="$t('contract.status') + ':'"
              :options="statusFilterOptions"
            />
          </v-row>
          </v-col>
        <v-spacer />
        <v-col cols="4" class="">
          <v-row align="center" justify="end" class="ma-1">
            <v-switch
              class="mt-0"
              dense
              hide-details
              v-model="filters.showOnlyUsersData"
            />
            <span>{{ $t('contract.showMine') }}</span>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <SkeletonTableLoader
      :items-length="filteredContracts.length"
      type="cards"
    >
      <v-row>
        <v-col
          sm="12"
          :md="cardSize(contract).md"
          :lg="cardSize(contract).lg"
          :xl="cardSize(contract).xl"
          v-for="contract in filteredContracts"
          :key="contract.id"
        >
          <component
            :is="contract.isParent ? 'MainContractCard' : 'ContractCard'"
            :contract="contract"
            :expanded="expandedCard.includes(contract.id)"
            @EXPAND="handleExpand(contract.id)"
            @COLLAPSE="handleCollapse(contract.id)"
          />
        </v-col>
      </v-row>
    </SkeletonTableLoader>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'

import { getContracts } from '@/services'
import { authContext } from '@/plugins/msalPlugin'

export default {
  name: 'ContractsView',

  data () {
    return {
      sortAsc: true,
      selectedSortType: 'name-asc',
      expandedCard: [],
      search: [],
      filters: {
        status: 'all',
        showOnlyUsersData: true
      }
    }
  },
  async mounted () {
    await getContracts()
  },
  computed: {
    sortOptions () {
      return [
        {
          label: this.$t('contract.name'),
          type: 'name-asc',
          compareFunction: (a, b) => a.contractName.localeCompare(b.contractName)
        },
        {
          label: this.$t('contract.price'),
          type: 'price-asc',
          compareFunction: (a, b) => a.signedPriceCents - b.signedPriceCents
        },
        {
          label: this.$t('contract.signatureDate'),
          type: 'signature-date-asc',
          compareFunction: (a, b) => new Date(a.signatureDate) - new Date(b.signatureDate)
        },
        {
          label: this.$t('contract.completionTime'),
          type: 'completion-date-asc',
          compareFunction: (a, b) => new Date(a.completionTime) - new Date(b.completionTime)
        }
      ]
    },
    selectedSort () {
      return this.sortOptions.find(option => option.type === this.selectedSortType) || this.sortOptions[0]
    },
    filterDefinitions () {
      return {
        status: [
          {
            value: 'all',
            label: this.$t('contract.filters.allContracts')
          },
          {
            value: 'active',
            label: this.$t('contract.filters.activeContracts')
          },
          {
            value: 'closed',
            label: this.$t('contract.filters.closedContracts')
          }
        ]
      }
    },
    statusFilterOptions () {
      return this.filterDefinitions.status
    },
    currentUsername () {
      return authContext.account.username.split('@')[0]
    },
    parsedSearches () {
      return this.search.reduce((acc, curr) => {
        const value = curr.item || curr
        const fieldName = curr.field || 'other'
        return acc[fieldName]
          ? { ...acc, [fieldName]: [...acc[fieldName], value] }
          : { ...acc, [fieldName]: [value] }
      }, {})
    },
    ...mapState({
      contracts: (state) => state.contracts.map(contract => {
        return {
          ...contract,
          parts: contract.isParent
            ? contract.subContracts.reduce((acc, curr) => [
              ...acc,
              ...curr.parts.map(i => ({ ...i, label: `${i.name} (${curr.contractName})` }))
            ], [])
            : contract.parts
        }
      }).filter(i => i.parts.length),
      projects: (state) => state.projects,
      contractTypes: (state) => state.contractTypes,
      packages: (state) => state.packages,
      contractors: (state) => state.contractors,
      employers: (state) => state.employers,
      areas: (state) => state.areas
    }),
    searchCriteria () {
      return [
        { header: this.$tc('contract.project.OFF', 2) },
        ...this.projects.reduce((acc, curr) => {
          return [
            ...acc,
            ...curr.areas.map((i) => ({
              item: { ...curr, area: i.code },
              field: 'project',
              key: `project-${curr.id}-${i.code}`,
              label: `${curr.name} ${curr.code ? `- ${curr.code}` : ''} ${
                this.areas.length > 1 ? `(${i.name})` : ''
              }`
            }))
          ]
        }, []),
        { header: this.$tc('contract.type', 2) },
        ...this.contractTypes.reduce((acc, curr) => {
          return [
            ...acc,
            ...curr.areas.map((i) => ({
              item: { ...curr, area: i.code },
              field: 'contractType',
              key: `contractType-${curr.id}-${i.code}`,
              label: `${curr.name} ${
                this.areas.length > 1 ? `(${i.name})` : ''
              }`
            }))
          ]
        }, []),
        { header: this.$tc('contract.package', 2) },
        ...this.packages.reduce((acc, curr) => {
          return [
            ...acc,
            ...curr.areas.map((i) => ({
              item: { ...curr, area: i.code },
              field: 'package',
              key: `package-${curr.id}-${i.code}`,
              label: `${curr.name} ${
                this.areas.length > 1 ? `(${i.name})` : ''
              }`
            }))
          ]
        }, []),
        { header: this.$tc('contract.contractor', 2) },
        ...this.contractors.map((i) => ({
          item: i,
          field: 'contractor',
          key: `contractor-${i.companyName}`,
          label: `${i.companyName} (${i.sapVendorNumber || ''})`
        })),
        { header: this.$tc('contract.employer', 2) },
        ...this.employers.map((i) => ({
          item: i,
          field: 'employer',
          key: `employer-${i.companyName}`,
          label: i.companyName
        }))
      ]
    },
    filteredContracts () {
      return [...this.contracts]
        .filter(this.filterByUserAssignment)
        .filter(this.filterByStatus)
        .filter(this.filterByProject)
        .filter(contract => this.filterByFieldAndArea(contract, 'package'))
        .filter(contract => this.filterByFieldAndArea(contract, 'contractType'))
        .filter(contract => this.filterByCompany(contract, 'employer'))
        .filter(contract => this.filterByCompany(contract, 'contractor'))
        .filter(this.filterByName)
        .sort(this.applySorting)
    }
  },
  methods: {
    filterByName (contract) {
      const searchTerms = this.parsedSearches.other
      if (!searchTerms) return true
      return searchTerms.some(term => {
        const upperTerm = term.toUpperCase()
        return contract.contractName.toUpperCase().includes(upperTerm) ||
          contract.subContracts.some(subContract =>
            subContract.contractName.toUpperCase().includes(upperTerm)
          )
      })
    },
    filterByCompany (contract, companyType) {
      const companies = this.parsedSearches[companyType]
      if (!companies) return true
      return companies.some(company => {
        const matchesMain = contract[companyType] &&
          company.companyName === contract[companyType].companyName
        const matchesSub = contract.subContracts.some(subContract =>
          company.companyName === subContract[companyType]?.companyName
        )
        return matchesMain || matchesSub
      })
    },
    filterByFieldAndArea (contract, fieldName) {
      const searchItems = this.parsedSearches[fieldName]
      if (!searchItems) return true
      return searchItems.some(searchItem => {
        const matchesMain = searchItem.name === contract[fieldName] &&
          searchItem.area === contract.area.code
        const matchesSub = contract.subContracts.some(subContract =>
          searchItem.name === subContract[fieldName] &&
          searchItem.area === subContract.area.code
        )
        return matchesMain || matchesSub
      })
    },
    filterByProject (contract) {
      const projects = this.parsedSearches.project
      if (!projects) return true
      return projects.some(project => {
        const matchesMain = contract.project && project.id === contract.project.id
        const matchesSub = contract.subContracts.some(subContract =>
          project.id === subContract.project?.id
        )
        return matchesMain || matchesSub
      })
    },
    filterByUserAssignment (contract) {
      if (!this.filters.showOnlyUsersData) return true
      const userRoles = ['contractManager', 'packageManager', 'projectController']
      const isUserAssigned = (item) => userRoles.some(role =>
        item[role] === this.currentUsername
      )
      return contract.isParent
        ? contract.subContracts.some(isUserAssigned)
        : isUserAssigned(contract)
    },
    filterByStatus (contract) {
      if (this.filters.status === 'all') return true
      const isActive = this.filters.status === 'active'
      const checkStatus = (item) => item.active === isActive
      return contract.isParent
        ? contract.subContracts.some(checkStatus)
        : checkStatus(contract)
    },
    applySorting (a, b) {
      const result = this.selectedSort.compareFunction(a, b)
      return this.sortAsc ? result : -result
    },
    handleExpand (index) {
      this.expandedCard.push(index)
    },
    handleCollapse (index) {
      this.expandedCard.splice(this.expandedCard.indexOf(index), 1)
    },
    handleAddContract () {
      this.$router.push({ name: 'new contract' })
    },
    cardSize (contract) {
      if (!this.expandedCard.includes(contract.id) || !contract.subContracts?.length) {
        return { md: 6, lg: 4, xl: 3 }
      }
      const subContractsCount = contract.subContracts.length
      const sizeMap = {
        1: { md: 6, lg: 4, xl: 3 },
        2: { md: 12, lg: 8, xl: 6 },
        3: { md: 12, lg: 12, xl: 9 }
      }
      return sizeMap[subContractsCount] || { md: 12, lg: 12, xl: 12 }
    }
  }
}
</script>
